import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Container, Button, Form, Modal, Pagination, InputGroup, Row, Col, Table, Spinner, Dropdown } from 'react-bootstrap';
import { 
  FaMapMarkerAlt,
  FaSearch, FaTimesCircle, FaPlus, FaTrash, FaEdit, 
  FaExclamationTriangle, FaBoxOpen, FaInfoCircle, 
  FaChevronDown, FaChevronUp, FaCapsules, FaCamera 
} from 'react-icons/fa';
import { get, post, put, del } from '../utils/api';
import { useToast } from './ToastContext';
import { handleError } from '../utils/errorHandler';
import _ from 'lodash';
import './MedicineManagement.css';
import config from '../config';
import ProgressDialog from './ProgressDialog';
import NoDataCard from './NoDataCard';

const MedicineManagement = () => {
  const { showToast } = useToast();
  const [medicineList, setMedicineList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentMedicine, setCurrentMedicine] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Form State
  const [form, setForm] = useState({
    name: '',
    generic_name: '',
    manufacturer: '',
    category: '',
    description: '',
    prescription_required: false,
    image_url: '',
    status: 'active',
    unit_name: 'piece',
    strip_conversion: 1,
    box_conversion: 1,
    hsn_code: '3004',
    gst_rate: 12,
  });

  // Search & Filter & Sort State
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRx, setFilterRx] = useState('all'); // all, rx, otc
  const [sortBy, setSortBy] = useState('name-asc'); // name-asc, name-desc, stock-desc, stock-asc
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  
  // Categories & Stats
  const [categoriesList, setCategoriesList] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    rx: 0,
    otc: 0,
    outOfStock: 0,
  });

  // Batch details expanded view
  const [expandedBatches, setExpandedBatches] = useState({}); // { medicineId: [batches] }
  const [loadingBatches, setLoadingBatches] = useState({}); // { medicineId: true/false }

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [medicineToDelete, setMedicineToDelete] = useState(null);
  const [imageLoadError, setImageLoadError] = useState({});
  const [modalImageError, setModalImageError] = useState(false);

  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    try {
      const origin = new URL(config.API_BASE_URL).origin;
      if (url.startsWith('/uploads') || url.startsWith('uploads')) {
        return `${origin}/api${url.startsWith('/') ? url : `/${url}`}`;
      }
      return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
    } catch (e) {
      return url;
    }
  };

  // Fetch unique categories and global stats count
  const loadStatsAndCategories = useCallback(async () => {
    try {
      const catRes = await get('/medicines/categories');
      if (catRes.success) {
        setCategoriesList(catRes.data || []);
      }
      
      const statsRes = await get('/medicines?limit=10000');
      if (statsRes.success && statsRes.data) {
        const list = statsRes.data;
        const total = list.length;
        const rx = list.filter(m => m.prescription_required || m.prescriptionRequired).length;
        const otc = total - rx;
        const outOfStock = list.filter(m => {
          const totalStock = m.batches?.reduce((sum, b) => sum + (parseInt(b.stock) || 0), 0) || 0;
          return totalStock === 0;
        }).length;
        
        setStats({ total, rx, otc, outOfStock });
      }
    } catch (e) {
      console.error('Failed to load stats/categories:', e);
    }
  }, []);

  const fetchMedicines = useCallback(async (query = '', category = '', rx = 'all', page = 1, showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    } else {
      setIsSearching(true);
    }
    setError(null);
    try {
      const response = await get(`/medicines?q=${query}&category=${category}&page=${page}&limit=12`);
      if (response.success || response.data) {
        let list = response.data || [];
        // Filter Rx Requirement locally
        if (rx === 'rx') {
          list = list.filter(m => m.prescription_required || m.prescriptionRequired);
        } else if (rx === 'otc') {
          list = list.filter(m => !(m.prescription_required || m.prescriptionRequired));
        }
        setMedicineList(list);

        const total = response.pagination?.total_items ?? 
                      response.pagination?.totalItems ?? 
                      response.pagination?.total ?? 
                      response.total ?? 
                      response.totalItems ?? 
                      response.count ?? 
                      (list ? list.length : 0);

        const pages = response.totalPages ?? 
                      response.pagination?.total_pages ?? 
                      response.pagination?.totalPages ?? 
                      response.total_pages ?? 
                      (total ? Math.ceil(total / 12) : 1);

        setTotalPages(Math.max(1, pages));
        setTotalItems(total);
      } else {
        const message = response.message || 'Failed to fetch medicines.';
        setError(message);
        showToast(message, 'warning');
      }
    } catch (error) {
      const message = handleError(error, 'Fetch Medicines', showToast);
      setError(message);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
      setIsSearching(false);
    }
  }, [showToast]);

  // Debounced search trigger
  const debouncedSearch = useMemo(() => {
    return _.debounce((val) => {
      setSearchQuery(val);
      setCurrentPage(1);
    }, 450);
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    debouncedSearch(val);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Run main query fetch when search/filters change
  useEffect(() => {
    fetchMedicines(searchQuery, filterCategory, filterRx, currentPage, true);
  }, [searchQuery, filterCategory, filterRx, currentPage, fetchMedicines]);

  // Initial load
  useEffect(() => {
    loadStatsAndCategories();
  }, [loadStatsAndCategories]);

  // Client-side Sorting calculation
  const sortedMedicines = useMemo(() => {
    let list = [...medicineList];
    if (sortBy === 'name-asc') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      list.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === 'stock-desc') {
      list.sort((a, b) => {
        const stockA = a.batches?.reduce((sum, bat) => sum + bat.stock, 0) || 0;
        const stockB = b.batches?.reduce((sum, bat) => sum + bat.stock, 0) || 0;
        return stockB - stockA;
      });
    } else if (sortBy === 'stock-asc') {
      list.sort((a, b) => {
        const stockA = a.batches?.reduce((sum, bat) => sum + bat.stock, 0) || 0;
        const stockB = b.batches?.reduce((sum, bat) => sum + bat.stock, 0) || 0;
        return stockA - stockB;
      });
    }
    return list;
  }, [medicineList, sortBy]);


  const handleShowModal = async (medicine = null) => {
    setCurrentMedicine(medicine);
    if (medicine) {
      try {
        setLoading(true);
        const response = await get(`/medicines/${medicine.medicine_id}`);
        if (response.success) {
          const { name, generic_name, manufacturer, category, description, prescription_required, image_url, status, unit_name, strip_conversion, box_conversion, hsn_code, gst_rate } = response.data;
          setForm({
            name: name || '',
            generic_name: generic_name || '',
            manufacturer: manufacturer || '',
            category: category || '',
            description: description || '',
            prescription_required: prescription_required || false,
            image_url: image_url || '',
            status: status || 'active',
            unit_name: unit_name || 'piece',
            strip_conversion: strip_conversion || 1,
            box_conversion: box_conversion || 1,
            hsn_code: hsn_code || '3004',
            gst_rate: gst_rate !== undefined && gst_rate !== null ? gst_rate : 12,
          });
          setImageFile(null);
          setModalImageError(false);
        } else {
          setError(response.message || 'Failed to fetch medicine details.');
        }
      } catch (error) {
        setError(error.message || 'Failed to fetch medicine details.');
      } finally {
        setLoading(false);
      }
    } else {
      setForm({
        name: '',
        generic_name: '',
        manufacturer: '',
        category: '',
        description: '',
        prescription_required: false,
        image_url: '',
        status: 'active',
        unit_name: 'piece',
        strip_conversion: 1,
        box_conversion: 1,
        hsn_code: '3004',
        gst_rate: 12,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!form.name || form.name.trim() === '') {
      setError('Medicine name is required.');
      setLoading(false);
      return;
    }

    try {
      let result;
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('generic_name', form.generic_name || '');
      formData.append('manufacturer', form.manufacturer);
      formData.append('category', form.category);
      formData.append('description', form.description);
      formData.append('prescription_required', form.prescription_required);
      formData.append('unit_name', form.unit_name);
      formData.append('strip_conversion', form.strip_conversion);
      formData.append('box_conversion', form.box_conversion);
      formData.append('hsn_code', form.hsn_code || '3004');
      formData.append('gst_rate', form.gst_rate !== undefined && form.gst_rate !== '' ? form.gst_rate : 12);
      if (!currentMedicine) {
        formData.append('status', 'active');
      } else {
        formData.append('status', form.status);
      }
      if (imageFile) {
        formData.append('medicineImage', imageFile);
      }

      if (currentMedicine) {
        result = await put(`/medicines/${currentMedicine.medicine_id}`, formData);
      } else {
        result = await post('/medicines', formData);
      }

      if (result.success) {
        showToast(result.message || 'Medicine saved successfully!', 'success');
        handleCloseModal();
        fetchMedicines(searchQuery, filterCategory, filterRx, currentPage, true);
        loadStatsAndCategories();
      } else {
        setError(result.message || 'Operation failed.');
      }
    } catch (error) {
      setError(error.message || 'Operation failed.');
    }
    setLoading(false);
  };

  const handleDelete = (medicineId) => {
    setMedicineToDelete(medicineId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (medicineToDelete) {
      setLoading(true);
      setError(null);
      try {
        const result = await del(`/medicines/${medicineToDelete}`);
        if (result.success) {
          showToast(result.message || 'Medicine deleted successfully!', 'success');
          fetchMedicines(searchQuery, filterCategory, filterRx, currentPage, true);
          loadStatsAndCategories();
        } else {
          setError(result.message || 'Deletion failed.');
        }
      } catch (error) {
        setError(error.message || 'Deletion failed.');
      }
      setLoading(false);
      setShowConfirmModal(false);
      setMedicineToDelete(null);
    }
  };

  const previewUrl = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    if (form.image_url) return resolveImageUrl(form.image_url);
    return null;
  }, [imageFile, form.image_url]);

  const handleChooseFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setImageFile(f);
    setModalImageError(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setForm(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
      setCurrentPage(pageNumber);
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          // Fallback for environments where smooth scrolling options object isn't supported
          window.scrollTo(0, 0);
        }
      }
    }
  };

  // Stock Summary Calculation
  const getMedicineStockSummary = (medicine) => {
    const totalPieces = medicine.batches?.reduce((sum, b) => sum + (parseInt(b.stock) || 0), 0) || 0;
    const baseUnit = medicine.unit_name || 'piece';
    
    if (totalPieces === 0) {
      return {
        status: 'out-of-stock',
        label: 'Out of Stock',
        color: 'var(--danger-color)',
        badgeClass: 'badge-out-of-stock'
      };
    }
    
    if (totalPieces <= 50) {
      return {
        status: 'low-stock',
        label: `Low Stock (${totalPieces} ${baseUnit}s)`,
        color: 'var(--warning-color)',
        badgeClass: 'badge-low-stock'
      };
    }
    
    return {
      status: 'in-stock',
      label: `In Stock (${totalPieces} ${baseUnit}s)`,
      color: 'var(--success-color)',
      badgeClass: 'badge-in-stock'
    };
  };

  // Lazy-load batches list per card
  const toggleBatches = async (medicineId) => {
    if (expandedBatches[medicineId]) {
      setExpandedBatches(prev => {
        const next = { ...prev };
        delete next[medicineId];
        return next;
      });
      return;
    }
    
    setLoadingBatches(prev => ({ ...prev, [medicineId]: true }));
    try {
      const response = await get(`/batches?medicine_id=${medicineId}`);
      if (response.success) {
        setExpandedBatches(prev => ({ ...prev, [medicineId]: response.data || [] }));
      } else {
        showToast(response.message || 'Failed to load batches.', 'error');
      }
    } catch (e) {
      showToast(e.message || 'Failed to load batches.', 'error');
    } finally {
      setLoadingBatches(prev => ({ ...prev, [medicineId]: false }));
    }
  };

  // Formatting batch stock in box/strip/pieces
  const formatBatchStock = (batch, medicine) => {
    const stock = parseInt(batch.stock_quantity || batch.stock || 0);
    const boxConversion = parseInt(medicine.box_conversion) || 1;
    const stripConversion = parseInt(medicine.strip_conversion) || 1;
    const baseUnit = medicine.unit_name || 'piece';
    
    if (boxConversion > 1 && stock >= boxConversion) {
      const boxes = Math.floor(stock / boxConversion);
      const rem = stock % boxConversion;
      if (rem === 0) return `${boxes} box${boxes > 1 ? 'es' : ''}`;
      if (stripConversion > 1 && rem >= stripConversion) {
        const strips = Math.floor(rem / stripConversion);
        const pieces = rem % stripConversion;
        return `${boxes} box${boxes > 1 ? 'es' : ''}, ${strips} strip${strips > 1 ? 's' : ''} ${pieces > 0 ? `+ ${pieces} ${baseUnit}s` : ''}`;
      }
      return `${boxes} box${boxes > 1 ? 'es' : ''} + ${rem} ${baseUnit}s`;
    }
    
    if (stripConversion > 1 && stock >= stripConversion) {
      const strips = Math.floor(stock / stripConversion);
      const pieces = stock % stripConversion;
      if (pieces === 0) return `${strips} strip${strips > 1 ? 's' : ''}`;
      return `${strips} strip${strips > 1 ? 's' : ''} + ${pieces} ${baseUnit}s`;
    }
    
    return `${stock} ${baseUnit}s`;
  };

  // Formatting Expiry date alert
  const getExpiryLabel = (dateString) => {
    if (!dateString) return '—';
    const expiryDate = new Date(dateString);
    const now = new Date();
    if (isNaN(expiryDate)) return '—';
    
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return <span className="expiry-alert expired">Expired</span>;
    }
    if (diffDays <= 90) {
      return <span className="expiry-alert expiring-soon">Expiring in {diffDays}d</span>;
    }
    return expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (error) return <Container className="medicine-container mt-3" style={{ color: 'red' }}>Error: {error}</Container>;

  return (
    <Container fluid className="medicine-container px-4 px-sm-5 py-3">
      {/* Page Header */}
      <div className="medicine-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="page-heading">Medicine Catalog</h2>
          <p className="page-subheading text-muted">Manage drug inventory, stock conversion tiers, and track batches.</p>
        </div>
        <Button variant="primary" onClick={() => handleShowModal()} className="btn-add-new card-hover">
          <FaPlus style={{ marginRight: '8px' }} /> Add Medicine
        </Button>
      </div>

      {/* Stats Summary Panel */}
      <Row className="mb-4 g-3 stats-summary-row">
        <Col xs={6} md={3}>
          <div className="stat-card total-medicines-card">
            <div className="stat-icon-wrapper"><FaBoxOpen /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Catalog</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card rx-card">
            <div className="stat-icon-wrapper"><span className="prescription-rx">Rx</span></div>
            <div className="stat-details">
              <span className="stat-value">{stats.rx}</span>
              <span className="stat-label">Prescription Drugs</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card otc-card">
            <div className="stat-icon-wrapper"><FaInfoCircle /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.otc}</span>
              <span className="stat-label">OTC (Over Counter)</span>
            </div>
          </div>
        </Col>
        <Col xs={6} md={3}>
          <div className="stat-card out-stock-card">
            <div className="stat-icon-wrapper"><FaExclamationTriangle /></div>
            <div className="stat-details">
              <span className="stat-value">{stats.outOfStock}</span>
              <span className="stat-label">Out of Stock</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Unified Filters Control Bar */}
      <div className="control-bar-wrapper mb-3 py-2 px-3 glassmorphic-panel">
        <Row className="g-2 align-items-center">
          <Col xs={12} md={3}>
            <InputGroup className="search-input-group compact">
              <InputGroup.Text className="bg-transparent border-end-0 py-1 px-2">
                <FaSearch className="text-muted" style={{ fontSize: '0.85rem' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search catalog..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="border-start-0 ps-0 py-1 search-input-field text-sm"
                style={{ fontSize: '0.85rem', height: '36px' }}
              />
              {searchTerm && (
                <Button variant="link" onClick={handleClearSearch} className="clear-search-btn text-muted border-start-0 py-0 px-2">
                  <FaTimesCircle style={{ fontSize: '0.85rem' }} />
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col xs={4} md={3}>
            <Dropdown className="w-100">
              <Dropdown.Toggle 
                variant="outline-secondary" 
                className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
              >
                {filterCategory ? `Category: ${filterCategory}` : 'Category: All'}
              </Dropdown.Toggle>
              <Dropdown.Menu 
                className="filter-dropdown-menu w-100"
                style={{ maxHeight: '240px', overflowY: 'auto' }}
              >
                <Dropdown.Item onClick={() => { setFilterCategory(''); setCurrentPage(1); }}>Category: All</Dropdown.Item>
                {categoriesList.map((cat, idx) => (
                  <Dropdown.Item 
                    key={idx} 
                    onClick={() => { setFilterCategory(cat); setCurrentPage(1); }}
                  >
                    {cat}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col xs={4} md={3}>
            <Dropdown className="w-100">
              <Dropdown.Toggle 
                variant="outline-secondary" 
                className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
              >
                {filterRx === 'rx' ? 'Type: Rx (Prescription)' : filterRx === 'otc' ? 'Type: OTC' : 'Type: All'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => { setFilterRx('all'); setCurrentPage(1); }}>Type: All</Dropdown.Item>
                <Dropdown.Item onClick={() => { setFilterRx('rx'); setCurrentPage(1); }}>Type: Rx (Prescription)</Dropdown.Item>
                <Dropdown.Item onClick={() => { setFilterRx('otc'); setCurrentPage(1); }}>Type: OTC</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col xs={4} md={3}>
            <Dropdown className="w-100">
              <Dropdown.Toggle 
                variant="outline-secondary" 
                className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
              >
                {sortBy === 'name-desc' ? 'Sort: Z - A' : sortBy === 'stock-desc' ? 'Sort: Stock (High)' : sortBy === 'stock-asc' ? 'Sort: Stock (Low)' : 'Sort: A - Z'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="filter-dropdown-menu w-100">
                <Dropdown.Item onClick={() => setSortBy('name-asc')}>Sort: A - Z</Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('name-desc')}>Sort: Z - A</Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('stock-desc')}>Sort: Stock (High)</Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('stock-asc')}>Sort: Stock (Low)</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </div>

      {isSearching && (
        <div className="d-flex justify-content-center align-items-center my-3">
          <ProgressDialog show={isSearching} message="Filtering list..." inline />
        </div>
      )}

      {/* Main Grid Card View */}
      <div className="medicines-grid">
        {sortedMedicines.length > 0 ? (
          sortedMedicines.map((medicine) => {
            const stockSummary = getMedicineStockSummary(medicine);
            const isBatchExpanded = !!expandedBatches[medicine.medicine_id];
            const isBatchLoading = !!loadingBatches[medicine.medicine_id];

            return (
              <div className={`medicine-card card-hover ${stockSummary.status}`} key={medicine.medicine_id}>
                {/* Upper Image Section */}
                <div className="medicine-image-container">
                  {medicine.image_url && !imageLoadError[medicine.medicine_id] ? (
                    <img
                      src={resolveImageUrl(medicine.image_url)}
                      alt={medicine.name}
                      onError={() => setImageLoadError(prev => ({ ...prev, [medicine.medicine_id]: true }))}
                    />
                  ) : (
                    <div className="image-placeholder">
                      <div className="placeholder-icon">💊</div>
                      <div className="placeholder-text">No Image</div>
                    </div>
                  )}
                  {/* Rx / OTC Badge */}
                  <span className={`rx-badge ${medicine.prescription_required ? 'rx-yes' : 'rx-no'}`}>
                    {medicine.prescription_required ? 'Rx' : 'OTC'}
                  </span>
                </div>

                {/* Details Section */}
                <div className="medicine-details">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h4 className="medicine-title" title={medicine.name}>{medicine.name}</h4>
                    <span className={`stock-badge ${stockSummary.badgeClass}`}>{stockSummary.label}</span>
                  </div>
                  
                  <div className="medicine-meta-info">
                    <div className="meta-line">
                      <span className="meta-label">Manufacturer</span>
                      <span className="meta-value">{medicine.manufacturer || '—'}</span>
                    </div>
                    <div className="meta-line">
                      <span className="meta-label">Category</span>
                      <span className="meta-value">
                        {medicine.categories?.map(cat => cat.name).join(', ') || '—'}
                      </span>
                    </div>
                    <div className="meta-line">
                      <span className="meta-label">UOM Packages</span>
                      <span className="meta-value uom-spec-text">
                        1 strip = {medicine.strip_conversion || 1} {medicine.unit_name || 'piece'}s • 
                        1 box = {medicine.box_conversion || 1}
                      </span>
                    </div>
                    <div className="meta-line">
                      <span className="meta-label">Tax / HSN</span>
                      <span className="meta-value">
                        <span className="badge bg-secondary me-1" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>HSN: {medicine.hsn_code || '3004'}</span>
                        <span className="badge bg-info text-dark" style={{ fontSize: '0.72rem', fontWeight: 600 }}>{medicine.gst_rate !== undefined && medicine.gst_rate !== null ? medicine.gst_rate : 12}% GST</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dynamic Expandable Batch Details */}
                <div className="batch-accordion-wrapper">
                  <button 
                    type="button" 
                    onClick={() => toggleBatches(medicine.medicine_id)} 
                    className="btn-toggle-batches d-flex justify-content-between align-items-center w-100"
                  >
                    <span>View Batch Stock Breakdown</span>
                    {isBatchExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  {isBatchExpanded && (
                    <div className="batch-accordion-content p-2">
                      {isBatchLoading ? (
                        <div className="text-center py-2"><Spinner animation="border" size="sm" /> Loading...</div>
                      ) : !expandedBatches[medicine.medicine_id] || expandedBatches[medicine.medicine_id].length === 0 ? (
                        <div className="text-center text-muted py-2 small">No active batches registered.</div>
                      ) : (
                        <Table size="sm" responsive className="batch-accordion-table mb-0">
                          <thead>
                            <tr>
                              <th>Batch</th>
                              <th>Stock</th>
                              <th>Expiry</th>
                              <th>Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expandedBatches[medicine.medicine_id].map((batch) => (
                              <tr key={batch.batch_id}>
                                <td><span className="fw-bold">{batch.batch_number}</span></td>
                                <td>{formatBatchStock(batch, medicine)}</td>
                                <td>{getExpiryLabel(batch.expiry_date || batch.expiry)}</td>
                                <td>₹{parseFloat(batch.selling_price || batch.price || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions Footer */}
                <div className="medicine-actions">
                  <Button size="sm" onClick={() => handleShowModal(medicine)} className="edit-btn-medicine card-hover">
                    <FaEdit /> Edit
                  </Button>
                  <Button size="sm" onClick={() => handleDelete(medicine.medicine_id)} className="delete-btn-medicine card-hover">
                    <FaTrash /> Delete
                  </Button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="grid-no-data py-5 text-center w-100">
            <NoDataCard
              title="No Medicines Found"
              message="Adjust filters or query to find drugs."
              icon="💊"
            />
          </div>
        )}
      </div>

      {/* Pagination Bar */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 rounded mt-4 glassmorphic-panel">
          <div className="text-muted small">
            Page <span className="fw-bold text-white">{currentPage}</span> of <span className="fw-bold text-white">{totalPages}</span>
            {totalItems > 0 && ` (${totalItems} total medicines)`}
          </div>
          <Pagination className="custom-pagination mb-0">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage <= 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1} />
            
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(pageNum => pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - currentPage) <= 2)
              .reduce((acc, pageNum, idx, arr) => {
                if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                  acc.push('ellipsis-' + pageNum);
                }
                acc.push(pageNum);
                return acc;
              }, [])
              .map(item => {
                if (typeof item === 'string') {
                  return <Pagination.Ellipsis key={item} disabled />;
                }
                return (
                  <Pagination.Item
                    key={item}
                    active={item === currentPage}
                    onClick={() => handlePageChange(item)}
                  >
                    {item}
                  </Pagination.Item>
                );
              })
            }
            
            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage >= totalPages} />
          </Pagination>
        </div>
      )}

      {/* Add / Edit Medicine Modal */}
      <Modal show={showModal} onHide={handleCloseModal} scrollable className="med-modal-modern" size="lg">
        <Modal.Header closeButton className="med-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="med-modal-icon">
              <FaCapsules />
            </div>
            <div>
              <Modal.Title className="med-modal-title mb-0">
                {currentMedicine ? 'Edit Medicine Configuration' : 'Register New Medicine'}
              </Modal.Title>
              <div className="med-modal-subtitle">
                {currentMedicine ? `Configuring ID #${currentMedicine.medicine_id} (${currentMedicine.name})` : 'Add a new pharmaceutical product to the inventory catalog'}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Form onSubmit={handleSubmit} className="med-modal-form-wrapper">
          <Modal.Body className="med-modal-body p-4">
            <Row className="g-4">
              {/* Left Column: Core Details & Packaging */}
              <Col lg={7}>
                <div className="med-form-section-title mb-3">
                  <span className="section-badge">1</span> Product Identity & Units
                </div>

                <Form.Group className="mb-3">
                  <Form.Label className="med-field-label">Medicine Commercial Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    name="name" 
                    value={form.name} 
                    onChange={handleChange} 
                    placeholder="e.g. Paracetamol 500mg" 
                    required 
                    className="med-modal-input"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="med-field-label">Active Ingredient / Composition (Generic Name)</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="generic_name" 
                    value={form.generic_name} 
                    onChange={handleChange} 
                    placeholder="e.g. Acetaminophen / Paracetamol" 
                    className="med-modal-input"
                  />
                </Form.Group>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="med-field-label">Manufacturer / Brand <span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        type="text" 
                        name="manufacturer" 
                        value={form.manufacturer} 
                        onChange={handleChange} 
                        required={!currentMedicine} 
                        placeholder="e.g. Pfizer, Cipla" 
                        className="med-modal-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="med-field-label">Category / Classification <span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        type="text" 
                        name="category" 
                        value={form.category} 
                        onChange={handleChange} 
                        required={!currentMedicine} 
                        placeholder="e.g. Analgesic, OTC (comma-separated)" 
                        className="med-modal-input"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="med-field-label">Base Dispensing Unit <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    name="unit_name" 
                    value={form.unit_name} 
                    onChange={handleChange} 
                    required
                    className="med-modal-select"
                  >
                    <option value="piece">Piece (Single Unit)</option>
                    <option value="tablet">Tablet</option>
                    <option value="capsule">Capsule</option>
                    <option value="bottle">Bottle (Liquid / Syrup)</option>
                    <option value="tube">Tube (Ointment / Gel)</option>
                    <option value="injection">Injection / Vial</option>
                    <option value="sachet">Sachet / Powder</option>
                  </Form.Select>
                </Form.Group>

                <div className="p-3 rounded mb-3 med-conversion-card">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="med-card-heading">Packaging Multipliers</span>
                    <span className="med-modal-hint-small">Base: {form.unit_name}</span>
                  </div>
                  <Row className="g-3">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="med-sub-label">Strip Factor</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="strip_conversion" 
                          value={form.strip_conversion} 
                          onChange={handleChange} 
                          min="1" 
                          required 
                          className="med-modal-input"
                        />
                        <span className="med-modal-hint-extra-small">units per strip</span>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="med-sub-label">Box Factor</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="box_conversion" 
                          value={form.box_conversion} 
                          onChange={handleChange} 
                          min="1" 
                          required 
                          className="med-modal-input"
                        />
                        <span className="med-modal-hint-extra-small">units per box</span>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 rounded mb-3 med-tax-card" style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '10px' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="med-card-heading" style={{ color: '#60a5fa', fontWeight: 600 }}>GST & Tax Classification</span>
                    <span className="med-modal-hint-small">Point-in-Time Snapshot</span>
                  </div>
                  <Row className="g-3">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="med-sub-label">HSN Code</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="hsn_code" 
                          value={form.hsn_code} 
                          onChange={handleChange} 
                          placeholder="e.g. 3004" 
                          className="med-modal-input"
                        />
                        <span className="med-modal-hint-extra-small">Standard Pharma: 3004</span>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="med-sub-label">GST Slab Rate (%)</Form.Label>
                        <Form.Select 
                          name="gst_rate" 
                          value={form.gst_rate} 
                          onChange={handleChange} 
                          className="med-modal-select"
                        >
                          <option value="0">0% (Nil / Exempt)</option>
                          <option value="5">5% (Life Saving)</option>
                          <option value="12">12% (Standard Pharma)</option>
                          <option value="18">18% (Supplements/Cosmetic)</option>
                          <option value="28">28% (Luxury / Other)</option>
                        </Form.Select>
                        <span className="med-modal-hint-extra-small">CGST: {(parseFloat(form.gst_rate || 0) / 2).toFixed(1)}% + SGST: {(parseFloat(form.gst_rate || 0) / 2).toFixed(1)}%</span>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="d-flex align-items-center justify-content-between p-3 rounded med-rx-card mb-3">
                  <div>
                    <div className="fw-bold text-white mb-0">Prescription Required (Rx)</div>
                    <div className="med-modal-hint-small">Mandatory doctor prescription verification before checkout</div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="prescription-req-switch"
                    name="prescription_required"
                    checked={form.prescription_required}
                    onChange={handleChange}
                    className="med-custom-switch"
                  />
                </div>

                {currentMedicine && (
                  <Form.Group className="mb-3">
                    <Form.Label className="med-field-label">Listing Status</Form.Label>
                    <Form.Select 
                      name="status" 
                      value={form.status} 
                      onChange={handleChange}
                      className="med-modal-select"
                    >
                      <option value="active">Active (Available in POS & Storefront)</option>
                      <option value="inactive">Inactive (Archived)</option>
                    </Form.Select>
                  </Form.Group>
                )}
              </Col>

              {/* Right Column: Image & Description */}
              <Col lg={5}>
                <div className="med-form-section-title mb-3">
                  <span className="section-badge">2</span> Media & Clinical Details
                </div>

                <div className="med-image-upload-card p-3 rounded mb-3 text-center">
                  <Form.Label className="med-field-label d-block text-start mb-2">Product Packaging Photo</Form.Label>
                  <div className="med-preview-viewport rounded mb-3 d-flex align-items-center justify-content-center">
                    {previewUrl && !modalImageError ? (
                      <img
                        src={previewUrl}
                        alt="Medicine Preview"
                        className="med-preview-img"
                        onError={() => setImageFile(null)}
                      />
                    ) : (
                      <div className="med-no-image-placeholder">
                        <FaCapsules className="fs-1 mb-2 text-muted" />
                        <span className="d-block small text-muted">No Image Selected</span>
                      </div>
                    )}
                  </div>

                  <input 
                    type="file" 
                    accept="image/png, image/jpeg, image/gif" 
                    ref={fileInputRef} 
                    style={{ display: 'none' }} 
                    onChange={handleFileChange} 
                  />

                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-primary" 
                      onClick={handleChooseFile} 
                      size="sm" 
                      className="w-100 fw-semibold"
                    >
                      <FaCamera className="me-1" /> {previewUrl ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    {previewUrl && (
                      <Button 
                        variant="outline-danger" 
                        onClick={handleRemoveImage} 
                        size="sm" 
                        className="fw-semibold px-3"
                        title="Remove Photo"
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                </div>

                <Form.Group>
                  <Form.Label className="med-field-label">Clinical Description & Indications</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    name="description" 
                    value={form.description} 
                    onChange={handleChange} 
                    placeholder="Enter active chemical formula, indications, dosage warnings, or storage guidelines..." 
                    className="med-modal-textarea"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="med-modal-footer d-flex justify-content-end py-3 px-4">
            <Button variant="primary" type="submit" className="med-submit-btn fw-bold px-4">
              {currentMedicine ? 'Update Medicine Configuration' : 'Register Medicine Product'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} dialogClassName="theme-dialog" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Product Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this medicine product? This will remove all associated batches from the catalog.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete Product</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MedicineManagement;